import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { workspaceApi, type CareEvent } from '../api/workspace';
import { EmptyState, Loading, PageError } from '../components/State';
import { EventRow } from '../components/EventRow';
import { todayIso } from '../lib/format';

type EventWithYouth = CareEvent & { youthName: string };

async function loadAllEvents(): Promise<EventWithYouth[]> {
  const clients = await workspaceApi.clients();
  const perClient = await Promise.all(
    clients.map(async (c) => {
      const evs = await workspaceApi.events(c.connectionId).catch(() => [] as CareEvent[]);
      const youthName = c.youthSnapshot?.name ?? 'Youth';
      return evs.map((e) => ({ ...e, youthName }));
    }),
  );
  return perClient.flat();
}

export function SchedulePage() {
  const [view, setView] = useState<'upcoming' | 'past'>('upcoming');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['schedule'],
    queryFn: loadAllEvents,
  });

  const { upcoming, past } = useMemo(() => {
    const today = todayIso();
    const all = data ?? [];
    return {
      upcoming: all
        .filter((e) => e.date >= today && e.status !== 'cancelled')
        .sort((a, b) => a.date.localeCompare(b.date)),
      past: all
        .filter((e) => e.date < today || e.status === 'cancelled')
        .sort((a, b) => b.date.localeCompare(a.date)),
    };
  }, [data]);

  if (isLoading) return <Loading />;

  const list = view === 'upcoming' ? upcoming : past;

  return (
    <div>
      <h1>Schedule</h1>
      {isError ? (
        <PageError error={error} onRetry={() => refetch()} />
      ) : (
        <>
          <div className="tabbar" style={{ marginTop: 8 }}>
            <button
              className={`tabbtn${view === 'upcoming' ? ' is-active' : ''}`}
              onClick={() => setView('upcoming')}
            >
              Upcoming ({upcoming.length})
            </button>
            <button
              className={`tabbtn${view === 'past' ? ' is-active' : ''}`}
              onClick={() => setView('past')}
            >
              Past ({past.length})
            </button>
          </div>

          {list.length === 0 ? (
            <EmptyState
              text={view === 'upcoming' ? 'Nothing scheduled.' : 'No past events.'}
            />
          ) : (
            <div className="yp__events">
              {list.map((e) => (
                <EventRow key={e.id} event={e} youthName={e.youthName} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
